import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faDollarSign,
  faBell,
  faCreditCard,
  faLock,
  faPhone,
  faUserPlus,
  faCommentDots,
  faNoteSticky,
  faShieldAlt,
  faGavel,
  faQuestionCircle,
  faPlus,
  faEllipsisV,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faFolder,
  faFolderPlus,
  faTimes,
  faExternalLinkAlt,
  faDownload,
  faEdit,
  faShare,
  faTrash,
  faArrowsAlt,
  faChevronRight as faGreaterThan,
  faSearch,
  faFile,
  faFileAlt,
  faPrint,
  faCopy,
  faPaste,
  faScissors,
  faExclamationTriangle,
  faSave,
  faTags,
  faFilm,
  faImage,
  faUpload,
  faEye,
  faEyeSlash,
  faSignOutAlt,
  faCopy as faCopyLink,
  faTrashAlt,
  faList,
  faFastBackward,
  faUsers,
  faArrowsUpDown,
  faCheck,
  faClone,
  faTrashCan,
  faICursor,
  faFileCsv,
  faFilePdf,
  faSliders,
  faPercent,
  faRectangleXmark,
  faFileLines,
  faGear,
  faUserGear,
  faKeyboard,
  faGlobe,
  faMap,
  faCirclePlay,
  faCircleInfo,
  faUserLock,
  faFileContract,
  faFolderOpen,
  faArrowUpFromBracket,
  faArrowUp,
  faSort,
  faArrowDownAZ,
  faArrowUpZA,
  faArrowDown19,
  faArrowUp91,
  faBullhorn,
  faPenToSquare,
  faCaretUp,
  faCaretDown,
  faRotateRight,
  faArrowLeftLong,
  faUpRightAndDownLeftFromCenter,
  faMoon,
  faSun,
  faHistory,
} from '@fortawesome/free-solid-svg-icons';
import {
  faGoogle,
  faApple,
  faFacebook,
  faSlideshare,
} from '@fortawesome/free-brands-svg-icons';

// Icon mapping - maps old icon names to Font Awesome icons
export const icons = {
  ProfileIcon: faUser,
  SubscriptionIcon: faDollarSign,
  NotificationIcon: faBell,
  PaymentIcon: faCreditCard,
  PasswordIcon: faLock,
  ContactIcon: faPhone,
  InviteIcon: faUserPlus,
  SupportIcon: faCommentDots,
  PrivacyIcon: faShieldAlt,
  TosIcon: faGavel,
  FaqIcon: faQuestionCircle,
  AddIcon: faPlus,
  MoreIcon: faEllipsisV,
  ExpandIcon: faChevronDown,
  Expand: faUpRightAndDownLeftFromCenter,
  ArrowBackIcon: faChevronLeft,
  ArrowBack: faChevronLeft,
  ArrowBackLong: faArrowLeftLong,
  ArrowForwardIcon: faChevronRight,
  FastRewindIcon: faFastBackward,
  FolderIcon: faFolder,
  NewFolder: faFolderPlus,
  CrossIcon: faTimes,
  CloseIcon: faTimes,
  OpenInNew: faExternalLinkAlt,
  Download: faDownload,
  DownloadIcon: faDownload,
  Edit: faEdit,
  Share: faShare,
  Delete: faTrash,
  DeleteOutlineIcon: faTrashAlt,
  MoveIcon: faArrowsAlt,
  MoveIcon2: faArrowsAlt,
  GreaterArrow: faGreaterThan,
  Search: faSearch,
  NewBudget: faFile,
  BudgetIcon: faFileAlt,
  PrinterIcon: faPrint,
  CopyLink: faCopyLink,
  CutIcon: faScissors,
  CopyIcon: faCopy,
  PasteIcon: faPaste,
  CommentIcon: faNoteSticky,
  DetailsIcon: faList,
  DuplicateIcon: faCopy,
  ShowMore: faChevronDown,
  Selector: faArrowsUpDown,
  HighlightedSelector: faArrowsUpDown,
  ErrorIcon: faExclamationTriangle,
  SaveIcon: faSave,
  CategoryIcon: faTags,
  CinemaTapeImage: faFilm,
  GoogleIcon: faGoogle,
  AppleIcon: faApple,
  FacebookIcon: faFacebook,
  ProfileImageIcon: faImage,
  UploadIcon: faUpload,
  VisibilityIcon: faEye,
  VisibilityOffIcon: faEyeSlash,
  EyeOn: faEye,
  EyeOff: faEyeSlash,
  LockIcon: faLock,
  SearchIcon: faSearch,
  Logout: faSignOutAlt,
  GroupsIcon: faUsers,
  CheckIcon: faCheck,
  // Menu icons
  CloneIcon: faClone,
  TrashCanIcon: faTrashCan,
  ICursorIcon: faICursor,
  'file-csv': faFileCsv,
  FileCsvIcon: faFileCsv,
  FilePdfIcon: faFilePdf,
  SlidersIcon: faSliders,
  PercentIcon: faPercent,
  DollarSignIcon: faDollarSign,
  RectangleXmarkIcon: faRectangleXmark,
  SlideshareIcon: faSlideshare,
  FileLinesIcon: faFileLines,
  GearIcon: faGear,
  UserGearIcon: faUserGear,
  KeyboardIcon: faKeyboard,
  GlobeIcon: faGlobe,
  MapIcon: faMap,
  CirclePlayIcon: faCirclePlay,
  InfoCircleIcon: faCircleInfo,
  UserLockIcon: faUserLock,
  FileContractIcon: faFileContract,
  FolderOpenIcon: faFolderOpen,
  ArrowUpFromBracketIcon: faArrowUpFromBracket,
  ArrowUpIcon: faArrowUp,
  SortIcon: faSort,
  ArrowDownAZIcon: faArrowDownAZ,
  ArrowUpZAIcon: faArrowUpZA,
  ArrowDown19Icon: faArrowDown19,
  ArrowUp91Icon: faArrowUp91,
  BullhornIcon: faBullhorn,
  PenToSquareIcon: faPenToSquare,
  CaretUpIcon: faCaretUp,
  CaretDownIcon: faCaretDown,
  RotateRightIcon: faRotateRight,
  HistoryIcon: faHistory,
  MoonIcon: faMoon,
  SunIcon: faSun,
};

// Helper component to render icons with consistent styling
export const Icon = ({ name, className, style, ...props }) => {
  const icon = icons[name];
  if (!icon) {
    console.warn(`Icon "${name}" not found in icon mapping`);
    return null;
  }
  // Merge default styles with user-provided styles
  // Ensure consistent sizing - enforce min/max after merging user styles
  const defaultStyle = {
    width: style?.width || '1em',
    height: style?.height || '1em',
    display: 'inline-block',
    verticalAlign: 'middle',
    fontSize: style?.fontSize || '1em', // Ensure consistent font size
    ...style,
    // leftMargin: '4px',
    margin: '0 2px',
    // leftPadding: '4px',
    // Enforce consistent sizing - these override any user-provided values
    minWidth: style?.minWidth || '1em',
    minHeight: style?.minHeight || '1em',
    maxWidth: style?.maxWidth || '1em',
    maxHeight: style?.maxHeight || '1em',
  };
  return (
    <FontAwesomeIcon
      icon={icon}
      className={className}
      style={defaultStyle}
      fixedWidth
      {...props}
    />
  );
};

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
};

Icon.defaultProps = {
  className: undefined,
  style: undefined,
};

// Export individual icon components for backward compatibility
export const ProfileIcon = props => <Icon name="ProfileIcon" {...props} />;
export const MoonIcon = props => <Icon name="MoonIcon" {...props} />;
export const SunIcon = props => <Icon name="SunIcon" {...props} />;
export const SubscriptionIcon = props => (
  <Icon name="SubscriptionIcon" {...props} />
);
export const NotificationIcon = props => (
  <Icon name="NotificationIcon" {...props} />
);
export const CheckIcon = props => <Icon name="CheckIcon" {...props} />;
export const PaymentIcon = props => <Icon name="PaymentIcon" {...props} />;
export const PasswordIcon = props => <Icon name="PasswordIcon" {...props} />;
export const ContactIcon = props => <Icon name="ContactIcon" {...props} />;
export const InviteIcon = props => <Icon name="InviteIcon" {...props} />;
export const SupportIcon = props => <Icon name="SupportIcon" {...props} />;
export const PrivacyIcon = props => <Icon name="PrivacyIcon" {...props} />;
export const TosIcon = props => <Icon name="TosIcon" {...props} />;
export const FaqIcon = props => <Icon name="FaqIcon" {...props} />;
export const AddIcon = props => <Icon name="AddIcon" {...props} />;
export const MoreIcon = props => <Icon name="MoreIcon" {...props} />;
export const ExpandIcon = props => <Icon name="ExpandIcon" {...props} />;
export const ArrowBackIcon = props => <Icon name="ArrowBackIcon" {...props} />;
export const ArrowBack = props => <Icon name="ArrowBack" {...props} />;
export const ArrowBackLong = props => (
  <Icon
    name="ArrowBackLong"
    {...props}
    // eslint-disable-next-line react/prop-types
    style={{ fontSize: '1.6em', ...props.style }}
  />
);
export const ArrowForwardIcon = props => (
  <Icon name="ArrowForwardIcon" {...props} />
);
export const FastRewindIcon = props => (
  <Icon name="FastRewindIcon" {...props} />
);
export const FolderIcon = props => <Icon name="FolderIcon" {...props} />;
export const NewFolder = props => <Icon name="NewFolder" {...props} />;
export const CrossIcon = props => <Icon name="CrossIcon" {...props} />;
export const CloseIcon = props => <Icon name="CloseIcon" {...props} />;
export const OpenInNew = props => <Icon name="OpenInNew" {...props} />;
export const Download = props => <Icon name="Download" {...props} />;
export const DownloadIcon = props => <Icon name="DownloadIcon" {...props} />;
export const Edit = props => <Icon name="Edit" {...props} />;
export const Share = props => <Icon name="Share" {...props} />;
export const Delete = props => <Icon name="Delete" {...props} />;
export const DeleteOutlineIcon = props => (
  <Icon name="DeleteOutlineIcon" {...props} />
);
export const MoveIcon = props => <Icon name="MoveIcon" {...props} />;
export const MoveIcon2 = props => <Icon name="MoveIcon2" {...props} />;
export const GreaterArrow = props => <Icon name="GreaterArrow" {...props} />;
export const Search = props => <Icon name="Search" {...props} />;
export const NewBudget = props => <Icon name="NewBudget" {...props} />;
export const BudgetIcon = props => <Icon name="BudgetIcon" {...props} />;
export const PrinterIcon = props => <Icon name="PrinterIcon" {...props} />;
export const CopyLink = props => <Icon name="CopyLink" {...props} />;
export const CommentIcon = props => (
  <Icon
    name="CommentIcon"
    style={{ fontSize: '1.2em', color: '#4285F4', ...props.style }}
    {...props}
  />
);
CommentIcon.propTypes = {
  style: PropTypes.object,
};
CommentIcon.defaultProps = {
  style: undefined,
};
export const DetailsIcon = props => <Icon name="DetailsIcon" {...props} />;
export const DuplicateIcon = props => <Icon name="DuplicateIcon" {...props} />;
export const ShowMore = props => <Icon name="ShowMore" {...props} />;
export const Selector = props => <Icon name="Selector" {...props} />;
export const HighlightedSelector = props => (
  <Icon name="HighlightedSelector" {...props} />
);
export const ErrorIcon = props => <Icon name="ErrorIcon" {...props} />;
export const SaveIcon = props => <Icon name="SaveIcon" {...props} />;
export const CategoryIcon = props => <Icon name="CategoryIcon" {...props} />;
export const CinemaTapeImage = props => (
  <Icon name="CinemaTapeImage" {...props} />
);
export const GoogleIcon = props => <Icon name="GoogleIcon" {...props} />;
export const AppleIcon = props => <Icon name="AppleIcon" {...props} />;
export const FacebookIcon = props => <Icon name="FacebookIcon" {...props} />;
export const ProfileImageIcon = props => (
  <Icon name="ProfileImageIcon" {...props} />
);
export const UploadIcon = props => <Icon name="UploadIcon" {...props} />;
export const VisibilityIcon = props => (
  <Icon name="VisibilityIcon" {...props} />
);
export const VisibilityOffIcon = props => (
  <Icon name="VisibilityOffIcon" {...props} />
);
export const EyeOn = props => <Icon name="EyeOn" {...props} />;
export const EyeOff = props => <Icon name="EyeOff" {...props} />;
export const LockIcon = props => <Icon name="LockIcon" {...props} />;
export const SearchIcon = props => <Icon name="SearchIcon" {...props} />;
export const Logout = props => <Icon name="Logout" {...props} />;
export const GroupsIcon = props => <Icon name="GroupsIcon" {...props} />;
export const Expand = props => <Icon name="Expand" {...props} />;
